/**
 * Timeline math shared by the Technology Roadmap page and its admin page.
 *
 * The chart is a run of month columns across one or more calendar years. Every
 * position is an absolute column index counted from January of the first year
 * shown, so a bar from November into the following February is just a span of
 * four consecutive columns.
 */

export interface RoadmapSpan {
  start_year: number;
  start_month: number; // 1–12
  end_year: number;
  end_month: number; // 1–12
}

export const MONTH_ABBREVS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * Calendar years the roadmap shows: every year an item touches, and always the
 * current year and the next one, as one contiguous ascending run.
 */
export function roadmapYears(items: readonly RoadmapSpan[], today: Date = new Date()): number[] {
  const thisYear = today.getFullYear();
  let first = thisYear;
  let last = thisYear + 1;
  for (const item of items) {
    first = Math.min(first, item.start_year);
    last = Math.max(last, item.end_year);
  }
  const years: number[] = [];
  for (let y = first; y <= last; y++) years.push(y);
  return years;
}

/** 0-based column index of (year, month 1–12) on a chart whose first column is January of firstYear. */
export function monthIndex(year: number, month: number, firstYear: number): number {
  return (year - firstYear) * 12 + (month - 1);
}

/** Inverse of monthIndex. */
export function monthAt(index: number, firstYear: number): { year: number; month: number } {
  return { year: firstYear + Math.floor(index / 12), month: (index % 12) + 1 };
}

/** Text drawn on a bar: `MAR`, `JAN – MAR`, or `NOV 2026 – FEB 2027` when it crosses a year. */
export function spanLabel(span: RoadmapSpan): string {
  const start = MONTH_ABBREVS[span.start_month - 1];
  const end = MONTH_ABBREVS[span.end_month - 1];
  if (span.start_year !== span.end_year) return `${start} ${span.start_year} – ${end} ${span.end_year}`;
  if (span.start_month === span.end_month) return start;
  return `${start} – ${end}`;
}

/** Sort comparator: earlier start first, then earlier end. */
export function compareSpans(a: RoadmapSpan, b: RoadmapSpan): number {
  const startDiff = (a.start_year * 12 + a.start_month) - (b.start_year * 12 + b.start_month);
  if (startDiff !== 0) return startDiff;
  return (a.end_year * 12 + a.end_month) - (b.end_year * 12 + b.end_month);
}

/**
 * Where today falls, in column units from the left edge (e.g. 13.5 = halfway
 * through the 14th column). Null when today is off the chart.
 */
export function todayPosition(today: Date, firstYear: number, totalMonths: number): number | null {
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const position = monthIndex(today.getFullYear(), today.getMonth() + 1, firstYear) + today.getDate() / daysInMonth;
  if (position < 0 || position > totalMonths) return null;
  return position;
}
